// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { SyntheticEvent } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  spacing,
  mediaMax,
  borderRadius,
  fontSize,
} from 'presentations/views/styles';
import {
  blackFix,
  gray01,
  gray04,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import { FormPageItem } from 'presentations/api/schema/form';
import { TeamItem } from 'presentations/api/schema/team';
import QuestionFormListItem from 'presentations/views/components/molecules/QuestionFormListItem';
import { VisualViewType } from 'domains/models/form/visualViewType';
import Button from 'presentations/views/components/atoms/Button';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Icon from 'presentations/views/components/atoms/Icon';
import { Trail } from 'presentations/views/components/atoms/Transition';
import Headline from 'presentations/views/components/atoms/Headline';
import Workflow from 'domains/models/workflow';
import { ErrorList } from 'domains/models';
import { ViewerItem } from 'presentations/api/schema/user';
import LinearProgress from 'presentations/views/components/atoms/LinearProgress';
import RichReadView from 'presentations/views/components/atoms/RichReadView';
import { imagePath } from 'presentations/assets';
import {
  defaultWidth,
  defaultHeight,
} from 'domains/models/event_type/coverImage';

export interface Props {
  viewer: ViewerItem | null;
  form: FormPageItem;
  team: TeamItem;
  onClickNext: (e: SyntheticEvent<HTMLInputElement>) => void;
  isLightColor: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  isComplete?: boolean;
  isWidget?: boolean;

  page: number;
  customQuestions: Workflow[];
  email: string | null;
  name: string | null;
  phoneNumber: string | null;
  customQuestionAnswers: any[];
  customOtherAnswers: string[];
  errors: ErrorList;

  onSubmitForm: (e: SyntheticEvent<HTMLFormElement>) => void;
  onClickPrev: () => void;
  onChange: (e: any) => void;
  onChangeOtherAnswer: (e: any) => void;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const isColored = props.form.visualViewType === VisualViewType.Colored;

  if (props.page === 0) {
    return (
      <PageContainer
        isColored={isColored}
        isWidget={props.isWidget}
        isShowImage={!!props.form.coverImage && props.page === 0}
      >
        <FirstView isColored={isColored}>
          {props.form.coverImage && (
            <EventMainVisualContainer>
              <Cover>
                <CoverImage
                  role="img"
                  aria-busy={true}
                  style={{
                    backgroundImage: `url(${imagePath(props.form.coverImage, {
                      width: defaultWidth,
                      height: defaultHeight,
                      fit: 'crop',
                      dpr: 2,
                    })})`,
                  }}
                />
              </Cover>
            </EventMainVisualContainer>
          )}
          <FirstViewText isColored={isColored}>
            <TitleContainer>
              <TitleHeadline variant="h1">{props.form.name}</TitleHeadline>
            </TitleContainer>
            {props.form.description && (
              <DescriptionContainer>
                <RichReadView
                  className="jicoo-editor-body jicoo-editor-view"
                  content={props.form.description}
                />
              </DescriptionContainer>
            )}

            <FirstViewSubmitContainer>
              <SubmitButton
                themeColor={props.form.color}
                isLightColor={props.isLightColor}
                onClick={props.onClickNext}
              >
                {t('Next')}
              </SubmitButton>
            </FirstViewSubmitContainer>
          </FirstViewText>
        </FirstView>
      </PageContainer>
    );
  }
  return (
    <PageContainer
      isColored={isColored}
      isWidget={props.isWidget}
      isShowImage={!!props.form.coverImage && props.page === 0}
    >
      <MainContainer>
        <BackIconButton onClick={props.onClickPrev} color={props.form.color}>
          <BackIcon name="chevron-left" />
        </BackIconButton>
        <ProgressContainer>
          <Bar
            value={(props.page / props.customQuestions.length) * 100}
            variant="determinate"
            color={props.form.color}
          />
          <PageNumber>
            {props.page}/{props.customQuestions.length}
          </PageNumber>
        </ProgressContainer>
        <MainScreen isColored={isColored}>
          <form onSubmit={props.onSubmitForm}>
            {props.customQuestions.map((question, index) => {
              if (props.page !== index + 1) {
                return null;
              }
              return (
                <QuestionContainer key={question.uid}>
                  <Trail isOpen>
                    <QuestionFormListItem
                      viewer={props.viewer}
                      email={props.email}
                      name={props.name}
                      phoneNumber={props.phoneNumber}
                      isColored={isColored}
                      isFullWidth={true}
                      question={question}
                      color={props.form.color}
                      index={index}
                      currentIndex={props.page - 1}
                      customOtherAnswers={props.customOtherAnswers}
                      customQuestionAnswers={props.customQuestionAnswers}
                      onChange={props.onChange}
                      onChangeOtherAnswer={props.onChangeOtherAnswer}
                      errors={props.errors}
                    />
                  </Trail>
                </QuestionContainer>
              );
            })}
            <SubmitContainer>
              <SubmitButton
                themeColor={props.form.color}
                isLightColor={props.isLightColor}
                onClick={props.onClickNext}
                isLoading={props.isLoading}
                isDisabled={props.isDisabled}
              >
                {props.isComplete ? t('Complete') : t('Next')}
              </SubmitButton>
            </SubmitContainer>
          </form>
        </MainScreen>
      </MainContainer>
    </PageContainer>
  );
}

const FirstView = styled.div<{ isColored?: boolean }>`
  overflow: hidden;
  border-radius: ${borderRadius(16)};
  border: 0;
  height: 100%;
  display: flex;
  justify-content: space-between;
  flex-direction: row-reverse;
  height: 100%;
  border: 1px solid ${gray04};
  ${mediaMax.tablet} {
    display: block;
  }
`;
const PageContainer = styled.div<{
  isColored?: boolean;
  isWidget?: boolean;
  isShowImage?: boolean;
}>`
  ${(props) =>
    props.isWidget
      ? css`
          padding: 0;
          max-width: 960px;
        `
      : css`
          padding: ${spacing(48)} 0 0;
          width: 960px;
        `}
  background-color: ${white};
  margin: 0 auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${mediaMax.tablet} {
    width: 100%;
    padding: ${spacing(24)} ${spacing(12)} 0;
  }
  ${(props) =>
    props.isShowImage &&
    css`
      ${mediaMax.tablet} {
        width: 100%;
      }
    `};
`;
const MainScreen = styled.div<{ isColored?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const MainContainer = styled.div`
  position: relative;
  max-width: 960px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  border-radius: ${borderRadius(16)};
  border: 1px solid ${gray04};
  position: relative;
  padding: ${spacing(120)} 0;
`;

const SubmitContainer = styled.div`
  margin-top: ${spacing(24)};
  padding: 0 ${spacing(40)};
  ${mediaMax.tablet} {
    padding: 0 ${spacing(28)};
    margin-top: 0;
    padding: 0 ${spacing(24)};
  }
`;

const BackIconButton = styled(IconButton)<{ color?: string }>`
  position: absolute;
  top: 24px;
  left: 24px;
  color: ${(props) => props.color};
  z-index: 2;
  ${mediaMax.tablet} {
    top: 0px;
    left: 0px;
  }
`;
const BackIcon = styled(Icon)`
  color: ${primary};
  width: 28px !important;
  height: 28px !important;
  color: inherit;
`;
const QuestionContainer = styled.div`
  margin-bottom: ${spacing(40)};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(24)};
  }
  padding: 0 ${spacing(40)};
  margin-bottom: 0;
  ${mediaMax.tablet} {
    padding: 0 ${spacing(24)};
  }
`;

const SubmitButton = styled(Button)<{
  themeColor?: string;
  isLightColor?: boolean;
}>`
  background-color: ${(props) => props.themeColor || ''};
  color: ${(props) => (props.isLightColor ? blackFix : whiteFix)};
  :hover {
    background-color: ${(props) => props.themeColor || ''};
    color: ${(props) => (props.isLightColor ? blackFix : whiteFix)};
  }

  height: 46px;
  font-size: ${fontSize(14)};
  min-width: 100px;
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 32px;
  right: 24px;
  justify-content: center;
  z-index: 1;
  ${mediaMax.tablet} {
    top: 12px;
    right: 12px;
  }
`;
const Bar = styled(LinearProgress)`
  width: 100px;
`;
const PageNumber = styled.div`
  font-size: ${fontSize(12)};
  color: ${gray01};
  margin-left: ${spacing(16)};
  display: flex;
  align-items: center;
`;

const FirstViewText = styled.div<{ isColored?: boolean }>`
  flex: 1;
  padding: ${spacing(40)} 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${spacing(120)} 0;
  ${mediaMax.tablet} {
    margin: 0 ${spacing(12)};
    padding: ${spacing(48)} 0;
  }
`;
const TitleHeadline = styled(Headline)`
  font-size: ${fontSize(20)};
  line-height: 1.4;
`;
const DescriptionContainer = styled.div<{ isColored?: boolean }>`
  margin: ${spacing(16)} ${spacing(40)} 0;

  ${mediaMax.tablet} {
    margin: ${spacing(16)} ${spacing(16)} 0;
  }
`;
const TitleContainer = styled.div<{ isColored?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0 ${spacing(40)};
  ${mediaMax.tablet} {
    margin: 0 ${spacing(16)};
  }
`;

const EventMainVisualContainer = styled.div`
  flex: 1;
  margin-bottom: 0;
  height: auto;
  ${mediaMax.tablet} {
    height: 160px;
  }
`;
const FirstViewSubmitContainer = styled.div`
  margin-top: ${spacing(24)};
  padding: 0 ${spacing(40)};
  ${mediaMax.tablet} {
    padding: 0 ${spacing(16)};
  }
`;

const Cover = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;
const CoverImage = styled.div`
  background-size: cover;
  background-repeat: no-repeat !important;
  background-position: center;
  vertical-align: bottom !important;
  width: 100%;
  height: 100%;
`;
